import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { BRAND, waLink } from "../../lib/mockData";
import { OrnamentDivider } from "../common/Ornaments";
import { useCategories } from "../../hooks/useCatalog";

export default function Footer() {
  const { data: categories = [] } = useCategories();
  return (
    <footer className="bg-emerald text-cream mt-20" data-testid="site-footer">
      {/* Top ornamental band */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="lg:col-span-3">
            {/* The logo's wordmark is dark green — it vanishes on the emerald
                footer. Sit it on a white card so it stays legible. */}
            <div className="inline-flex items-center justify-center bg-white rounded-xl border border-gold/40 p-2">
              <img src={BRAND.logoWhite} alt={BRAND.name} className="h-24 w-auto block object-contain" />
            </div>
            <p className="mt-5 text-sm text-cream/80 leading-relaxed">
              HALALGIFHAMPERS — Premium halal gift hampers for every occasion in Singapore.
            </p>
            <a
              href={waLink("Hello HALALGIFHAMPERS")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 bg-gold text-emerald px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors"
              data-testid="footer-whatsapp-cta"
            >
              <MessageCircle size={16} />
              WhatsApp Us
            </a>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-2">
            <h3 className="font-serif text-xl text-gold mb-4">Quick Links</h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
              <li><Link to="/shop" className="hover:text-gold transition-colors">Shop</Link></li>
              <li><Link to="/track" className="hover:text-gold transition-colors">Track Order</Link></li>
              <li><Link to="/about" className="hover:text-gold transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="hover:text-gold transition-colors">Contact</Link></li>
              <li><Link to="/faq" className="hover:text-gold transition-colors">FAQ</Link></li>
              <li><Link to="/privacy" className="hover:text-gold transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-gold transition-colors">Terms &amp; Conditions</Link></li>
              <li>
                <Link
                  to="/admin"
                  className="text-cream/70 hover:text-gold transition-colors text-xs"
                  data-testid="footer-admin-link"
                >
                  Admin Portal
                </Link>
              </li>
            </ul>
          </div>

          {/* Categories */}
          <div className="lg:col-span-4">
            <h3 className="font-serif text-xl text-gold mb-4">Shop by Occasion</h3>
            {/* All active categories, straight from Firestore (ordered by
                displayOrder) — no cap, so admin-added categories show up here. */}
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm" data-testid="footer-categories">
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/category/${c.slug}`}
                    className="hover:text-gold transition-colors"
                    data-testid="footer-category-link"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h3 className="font-serif text-xl text-gold mb-4">Contact</h3>
            <ul className="space-y-3.5 text-sm text-cream/85">
              <li className="flex gap-3">
                <MapPin size={16} className="text-gold shrink-0 mt-0.5" />
                <span>{BRAND.address}</span>
              </li>
              <li className="flex gap-3">
                <Phone size={16} className="text-gold shrink-0 mt-0.5" />
                <a href={`tel:${BRAND.phoneRaw}`} className="hover:text-gold transition-colors">
                  {BRAND.phone}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail size={16} className="text-gold shrink-0 mt-0.5" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-gold transition-colors">
                  {BRAND.email}
                </a>
              </li>
              <li className="pt-2 text-xs text-cream/60">UEN: {BRAND.uen}</li>
            </ul>
          </div>
        </div>

        <OrnamentDivider className="my-10 text-gold" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-cream/60">
          <div>© {new Date().getFullYear()} HALALGIFHAMPERS. All rights reserved.</div>
          <div>Singapore • UEN {BRAND.uen}</div>
        </div>
      </div>
    </footer>
  );
}
